import {useCallback, useMemo, useState} from 'react';

import {useDomainConfig} from '../../../../provider/domain-config/domain-config-hook';
import {DomainNameEnum} from '../../../../provider/domain-config/domain-config-type';
import {useHelpLinks} from '../../../../provider/help-links/help-links-hook';
import {ShortLocaleNameEnum} from '../../../../provider/locale/locale-context-type';
import {useLocale} from '../../../../provider/locale/locale-hook';
import {useUser} from '../../../../provider/user/user-hook';
import {DashboardRecommendationTypeEnum} from '../../../../service/dashboard/dashboard-recommendations/dashboard-recommendations-type';

import {
    knowledgeHelpVideoSchema,
    KnowledgeHelpVideoType,
    knowledgeMarketingEventSchema,
    KnowledgeMarketingEventType,
} from './knowledge-help-videos-type';

// eslint-disable-next-line sonarjs/cognitive-complexity
export function useKnowledgeVideos(defaultActiveRecommendationKey?: string | void): {
    recommendationVideoLinks: Array<KnowledgeHelpVideoType>;
    activeItemIndex: number;
    setActiveItemIndex: (number: number) => void;
    currentActiveItem: KnowledgeHelpVideoType | null;
    dashboardRecommendationLinkExists: (widgetKey: DashboardRecommendationTypeEnum) => boolean;
} {
    const helpLinks = useHelpLinks();

    const {domainName} = useDomainConfig();
    const {user} = useUser();
    const {shortLocaleName} = useLocale();

    const recommendationVideoLinks: Array<KnowledgeHelpVideoType> = useMemo(() => {
        const recommendationVideoLinksByDomain: Array<KnowledgeHelpVideoType> = helpLinks
            .filter((helpLink) => {
                return /^\^cp-recommendation-videos-(.+)\$$/.test(helpLink.urlPattern);
            })
            .flatMap((helpLink) => {
                const parseResult = knowledgeHelpVideoSchema.safeParse(helpLink);

                if (parseResult.success) {
                    return [parseResult.data];
                }

                return [];
            })
            .filter((helpLink) => {
                return helpLink.config.visibleForCustomers || user?.isStaff;
            })
            .sort((item1, item2) => (item2.config.priority ?? 0) - (item1.config.priority ?? 0));

        const userLocaleVideoLinks = recommendationVideoLinksByDomain.filter((helpLink) => {
            return helpLink.language === shortLocaleName;
        });

        const enLocaleVideoLinks = recommendationVideoLinksByDomain.filter((helpLink) => {
            return helpLink.language === ShortLocaleNameEnum.en;
        });

        if (userLocaleVideoLinks.length > 0) {
            return userLocaleVideoLinks;
        }

        if (shortLocaleName !== ShortLocaleNameEnum.ru) {
            return enLocaleVideoLinks;
        }

        return [];
    }, [helpLinks, shortLocaleName, user?.isStaff]);

    const dashboardRecommendationLinkExists = useCallback(
        (dashboardRecommendationKey: DashboardRecommendationTypeEnum) => {
            if (domainName !== DomainNameEnum.rocketData && domainName !== DomainNameEnum.brandWizard) {
                return false;
            }

            return recommendationVideoLinks.some((documentationLink) => {
                if (
                    [DashboardRecommendationTypeEnum.logo, DashboardRecommendationTypeEnum.cover].includes(
                        dashboardRecommendationKey
                    )
                ) {
                    return documentationLink.urlPattern === '^cp-recommendation-videos-logo_cover$';
                }

                if (
                    [DashboardRecommendationTypeEnum.yandexPosts, DashboardRecommendationTypeEnum.googlePosts].includes(
                        dashboardRecommendationKey
                    )
                ) {
                    return documentationLink.urlPattern === '^cp-recommendation-videos-posts$';
                }

                return documentationLink.urlPattern === `^cp-recommendation-videos-${dashboardRecommendationKey}$`;
            });
        },
        [domainName, recommendationVideoLinks]
    );

    const [activeItemIndex, setActiveItemIndex] = useState(
        defaultActiveRecommendationKey
            ? recommendationVideoLinks.findIndex((item) => {
                  return item.urlPattern === `^cp-recommendation-videos-${defaultActiveRecommendationKey}$`;
              }) || 0
            : 0
    );

    return {
        recommendationVideoLinks,
        activeItemIndex,
        setActiveItemIndex,
        currentActiveItem: recommendationVideoLinks[activeItemIndex] || null,
        dashboardRecommendationLinkExists,
    };
}

const marketingEventLinkId = '^cp-future-marketing-event-help$';

export function useFutureMarketingEvent(): KnowledgeMarketingEventType | null {
    const helpLinks = useHelpLinks();
    const {shortLocaleName} = useLocale();
    const {user} = useUser();

    return useMemo(() => {
        const marketingEventLinks: Array<KnowledgeMarketingEventType> = helpLinks
            .filter((helpLink) => {
                return helpLink.urlPattern === marketingEventLinkId;
            })
            .flatMap((helpLink) => {
                const parseResult = knowledgeMarketingEventSchema.safeParse(helpLink);

                if (parseResult.success) {
                    return [parseResult.data];
                }

                return [];
            });

        const linkWithUserLocale = marketingEventLinks.find((helpLink) => {
            return helpLink.language === shortLocaleName;
        });

        const linkWithEnLocale = marketingEventLinks.find((helpLink) => {
            return helpLink.language === ShortLocaleNameEnum.en;
        });

        if (linkWithUserLocale) {
            return linkWithUserLocale.config.visibleForCustomers || user?.isStaff ? linkWithUserLocale : null;
        }

        if (linkWithEnLocale && shortLocaleName !== ShortLocaleNameEnum.ru) {
            return linkWithEnLocale.config.visibleForCustomers || user?.isStaff ? linkWithEnLocale : null;
        }

        return null;
    }, [helpLinks, shortLocaleName, user?.isStaff]);
}
